const chapter = {
  id: 'circles',
  slug: 'circles',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Circles',
        hindi: 'वृत्त',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main, 1 in Advanced',
        hindi: 'जेईई मेन में 1-2 प्रश्न, एडवांस्ड में 1',
  },
  syllabusTags: ['Coordinate Geometry', 'JEE-Main', 'JEE-Advanced', 'Class-11'],
  sections: [
    {
      id: 'definition',
      title: {
        english: 'Definition & Standard Forms',
        hindi: 'परिभाषा और मानक रूप',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Circle',
        hindi: 'घेरा',
          },
          body: {
            english: 'The locus of all points equidistant from a fixed point (centre) is a **circle**. The constant distance is the **radius** $r$.',
        hindi: 'एक निश्चित बिंदु (केंद्र) से समान दूरी पर स्थित सभी बिंदुओं का स्थान एक **वृत्त** होता है। स्थिर दूरी **त्रिज्या** $r$ है।',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 360 320" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="170" x2="340" y2="170" stroke="#9ca3af" stroke-width="1"/>
  <line x1="180" y1="300" x2="180" y2="20" stroke="#9ca3af" stroke-width="1"/>
  <circle cx="180" cy="170" r="100" fill="none" stroke="#4338ca" stroke-width="2.2"/>
  <circle cx="180" cy="170" r="3.5" fill="#1f2937"/>
  <text x="158" y="188" font-size="12" fill="#1f2937">C(h, k)</text>
  <line x1="180" y1="170" x2="266.6" y2="120.0" stroke="#dc2626" stroke-width="2"/>
  <text x="215.30127018922195" y="139" font-size="12" fill="#b91c1c">r</text>
  <circle cx="266.6" cy="120.0" r="4" fill="#10b981"/>
  <text x="272.6025403784439" y="114" font-size="12" fill="#047857">P</text>
  <line x1="306.6" y1="189.3" x2="226.6" y2="50.7" stroke="#10b981" stroke-width="2"/>
  <text x="196.6025403784439" y="64.7179676972449" font-size="11" fill="#047857">tangent at P</text>
  <text x="180" y="305" font-size="12" fill="#6b7280" text-anchor="middle">Standard form: (x − h)² + (y − k)² = r²</text>
</svg>`,
          caption: {
            english: 'A circle of radius r centered at C(h, k); the tangent at any point P is perpendicular to the radius CP.',
        hindi: 'त्रिज्या r का एक वृत्त C(h, k) पर केन्द्रित है; किसी भी बिंदु P पर स्पर्शरेखा त्रिज्या CP के लंबवत है।',
          },
        },
        {
          type: 'table',
          headers: ['Form', 'Equation', 'Conditions'],
          rows: [
            ['Centred at origin', '$x^2 + y^2 = r^2$', '–'],
            ['Centred at $(h, k)$', '$(x - h)^2 + (y - k)^2 = r^2$', '–'],
            ['General', '$x^2 + y^2 + 2gx + 2fy + c = 0$', 'Centre $(-g, -f)$, radius $\\sqrt{g^2 + f^2 - c}$'],
            ['Diameter form', '$(x - x_1)(x - x_2) + (y - y_1)(y - y_2) = 0$', 'Endpoints of diameter $(x_1, y_1), (x_2, y_2)$'],
            ['Parametric', '$x = h + r\\cos\\theta,\\ y = k + r\\sin\\theta$', '$\\theta \\in [0, 2\\pi)$'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'For $x^2 + y^2 + 2gx + 2fy + c = 0$ to represent a real circle, $g^2 + f^2 - c > 0$. If $= 0$, it is a point circle; if $< 0$, no real locus.',
        hindi: 'एक वास्तविक वृत्त का प्रतिनिधित्व करने के लिए $x^2 + y^2 + 2gx + 2fy + c = 0$ के लिए, $g^2 + f^2 - c > 0$। यदि $= 0$, तो यह एक बिंदु वृत्त है; यदि $< 0$, कोई वास्तविक स्थान नहीं।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Find centre and radius of $x^2 + y^2 - 4x + 6y - 12 = 0$.',
        hindi: '$x^2 + y^2 - 4x + 6y - 12 = 0$ का केंद्र और त्रिज्या ज्ञात कीजिए।',
          },
          solution: {
            english: '$2g = -4, 2f = 6 \\Rightarrow g = -2, f = 3$. Centre $(2, -3)$. $r = \\sqrt{4 + 9 + 12} = 5$.',
        hindi: '$2जी = -4, 2एफ = 6 \\Rightarrow जी = -2, एफ = 3$। केंद्र $(2, -3)$. $r = \\sqrt{4 + 9 + 12} = 5$.',
          },
        },
      ],
    },
    {
      id: 'position-of-point',
      title: {
        english: 'Position of a Point w.r.t. a Circle',
        hindi: 'वृत्त के सापेक्ष बिन्दु की स्थिति',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Let $S \\equiv x^2 + y^2 + 2gx + 2fy + c$. Then for a point $P(x_1, y_1)$, evaluate $S_1 = S(x_1, y_1)$:',
        hindi: 'चलो $S \\equiv x^2 + y^2 + 2gx + 2fy + c$. फिर एक बिंदु $P(x_1, y_1)$ के लिए, $S_1 = S(x_1, y_1)$ का मूल्यांकन करें:',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$S_1 < 0$: $P$ lies **inside** the circle.',
        hindi: '$S_1 < 0$: $P$ वृत्त के **अंदर** स्थित है।',
            },
            {
              english: '$S_1 = 0$: $P$ lies **on** the circle.',
        hindi: '$S_1 = 0$: $P$ वृत्त पर ** स्थित है।',
            },
            {
              english: '$S_1 > 0$: $P$ lies **outside** the circle.',
        hindi: '$S_1 > 0$: $P$ वृत्त के **बाहर** स्थित है।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Where does $(2, 3)$ lie w.r.t. $x^2 + y^2 - 4x - 6y + 9 = 0$?',
        hindi: '$(2,3)$ w.r.t. कहाँ स्थित है? $x^2 ​​+ y^2 - 4x - 6y + 9 = 0$?',
          },
          solution: {
            english: '$S_1 = 4 + 9 - 8 - 18 + 9 = -4 < 0$. **Inside** the circle.',
        hindi: '$S_1 = 4 + 9 - 8 - 18 + 9 = -4 < 0$. **अंदर** वृत्त।',
          },
        },
      ],
    },
    {
      id: 'line-circle',
      title: {
        english: 'Line and a Circle',
        hindi: 'रेखा और वृत्त',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Let $d$ = perpendicular distance from centre to line. Compare with radius $r$:',
        hindi: 'मान लीजिए $d$ = केंद्र से रेखा तक लंबवत दूरी। त्रिज्या $r$ से तुलना करें:',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$d > r$: line does **not intersect** the circle.',
        hindi: '$d > r$: रेखा वृत्त को **नहीं** काटती है।',
            },
            {
              english: '$d = r$: line is **tangent** (touches at one point).',
        hindi: '$d = r$: रेखा **स्पर्शरेखा** है (एक बिंदु पर स्पर्श करती है)।',
            },
            {
              english: '$d < r$: line is a **secant** (cuts at two points). Length of chord $= 2\\sqrt{r^2 - d^2}$.',
        hindi: '$d < r$: रेखा एक **सेकेंट** है (दो बिंदुओं पर कटती है)। तार की लंबाई $= 2\\sqrt{r^2 - d^2}$.',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Condition for tangency',
        hindi: 'स्पर्शरेखा के लिए शर्त',
          },
          statement: {
            english: 'Line $y = mx + c$ is tangent to $x^2 + y^2 = r^2$ iff $c^2 = r^2(1 + m^2)$.',
        hindi: 'यदि $c^2 = r^2(1 + m^2)$ है तो रेखा $y = mx + c$ $x^2 + y^2 = r^2$ की स्पर्शरेखा है।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Find length of chord cut from $x^2 + y^2 = 25$ by line $3x + 4y = 5$.',
        hindi: 'लाइन $3x + 4y = 5$ द्वारा $x^2 + y^2 = 25$ से काटे गए तार की लंबाई ज्ञात करें।',
          },
          solution: {
            english: 'Centre $(0,0)$, radius $5$. $d = \\dfrac{|0 + 0 - 5|}{\\sqrt{25}} = 1$. Chord $= 2\\sqrt{25 - 1} = 4\\sqrt 6$.',
        hindi: 'केंद्र $(0,0)$, त्रिज्या $5$। $d = \\dfrac{|0 + 0 - 5|}{\\sqrt{25}} = 1$. कॉर्ड $= 2\\sqrt{25 - 1} = 4\\sqrt 6$.',
          },
        },
      ],
    },
    {
      id: 'tangent-normal',
      title: {
        english: 'Tangent, Normal & Chord of Contact',
        hindi: 'स्पर्श रेखा, अभिलम्ब और स्पर्श जीवा',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Tangent at a point on the circle',
        hindi: 'वृत्त पर एक बिंदु पर स्पर्शरेखा',
          },
          statement: {
            english: `Tangent at $(x_1, y_1)$ on $x^2 + y^2 = r^2$ is $xx_1 + yy_1 = r^2$.
For general circle $S \\equiv x^2 + y^2 + 2gx + 2fy + c = 0$: tangent at $(x_1, y_1)$ is
$$xx_1 + yy_1 + g(x + x_1) + f(y + y_1) + c = 0,$$ often written as $T = 0$.`,
            hindi: '$x^2 + y^2 = r^2$ पर $(x_1, y_1)$ पर स्पर्श रेखा $xx_1 + yy_1 = r^2$ है। सामान्य वृत्त $S \\equiv x^2 + y^2 + 2gx + 2fy + c = 0$ के लिए: $(x_1, y_1)$ पर स्पर्शरेखा है $$xx_1 + yy_1 + g(x + x_1) + f(y + y_1) + c = 0,$$ को अक्सर $T = 0$ लिखा जाता है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Tangents from an external point',
        hindi: 'किसी बाहरी बिंदु से स्पर्शरेखा',
          },
          statement: {
            english: 'From an external point $P(x_1, y_1)$, two tangents can be drawn. **Length** of each tangent: $L = \\sqrt{S_1}$ where $S_1 = x_1^2 + y_1^2 + 2gx_1 + 2fy_1 + c$.',
        hindi: 'एक बाहरी बिंदु $P(x_1, y_1)$ से, दो स्पर्श रेखाएं खींची जा सकती हैं। **प्रत्येक स्पर्शरेखा की लंबाई**: $L = \\sqrt{S_1}$ जहां $S_1 = x_1^2 + y_1^2 + 2gx_1 + 2fy_1 + c$।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Chord of Contact',
        hindi: 'संपर्क का तार',
          },
          statement: {
            english: 'The chord joining the points of contact of the two tangents from external point $P(x_1, y_1)$ has equation $T = 0$ (same form as tangent).',
        hindi: 'बाहरी बिंदु $P(x_1, y_1)$ से दो स्पर्शरेखाओं के संपर्क बिंदुओं को जोड़ने वाली जीवा का समीकरण $T = 0$ (स्पर्शरेखा के समान रूप) होता है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Pair of tangents from external point',
        hindi: 'बाह्य बिंदु से स्पर्श रेखाओं का युग्म',
          },
          statement: {
            english: 'The pair of tangents from $P(x_1, y_1)$ to circle $S = 0$ is given by $SS_1 = T^2$.',
        hindi: '$P(x_1, y_1)$ से वृत्त $S = 0$ तक स्पर्श रेखाओं का युग्म $SS_1 = T^2$ द्वारा दिया गया है।',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Find length of the tangent from $(5, 7)$ to $x^2 + y^2 = 9$.',
        hindi: '$(5, 7)$ से $x^2 + y^2 = 9$ तक स्पर्श रेखा की लंबाई ज्ञात कीजिए।',
          },
          solution: {
            english: '$L = \\sqrt{25 + 49 - 9} = \\sqrt{65}$.',
        hindi: '$L = \\sqrt{25 + 49 - 9} = \\sqrt{65}$.',
          },
        },
      ],
    },
    {
      id: 'chord-bisected',
      title: {
        english: 'Chord Bisected at a Given Point',
        hindi: 'दिये गये बिन्दु पर द्विभाजित जीवा',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Equation of chord with given midpoint',
        hindi: 'दिए गए मध्यबिंदु के साथ जीवा का समीकरण',
          },
          statement: {
            english: 'The chord of $S = 0$ bisected at the point $(x_1, y_1)$ inside the circle has equation $T = S_1$, where $T$ and $S_1$ are as defined above.',
        hindi: 'सर्कल के अंदर बिंदु $(x_1, y_1)$ पर द्विभाजित $S = 0$ की जीवा का समीकरण $T = S_1$ है, जहां $T$ और $S_1$ ऊपर परिभाषित हैं।',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'Find the equation of the chord of $x^2 + y^2 = 25$ bisected at $(3, 4)$.',
        hindi: '$x^2 + y^2 = 25$ की जीवा का $(3, 4)$ पर द्विभाजित समीकरण ज्ञात कीजिए।',
          },
          solution: {
            english: '$T : 3x + 4y = 25$. $S_1 = 9 + 16 - 25 = 0$. So chord: $3x + 4y = 25 + 0 = 25$. (Note: $(3,4)$ lies on circle, so the chord degenerates to the tangent.)',
        hindi: '$T : 3x + 4y = 25$। $S_1 = 9 + 16 - 25 = 0$. तो राग: $3x + 4y = 25 + 0 = 25$। (नोट: $(3,4)$ वृत्त पर स्थित है, इसलिए जीवा स्पर्शरेखा तक विकृत हो जाती है।)',
          },
        },
      ],
    },
    {
      id: 'family',
      title: {
        english: 'Family of Circles',
        hindi: 'वृत्तों का परिवार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Family through intersection of $S_1 = 0$ and $S_2 = 0$:  $S_1 + \\lambda S_2 = 0$.',
        hindi: '$S_1 = 0$ और $S_2 = 0$: $S_1 + \\lambda S_2 = 0$ के चौराहे से होकर गुजरने वाला परिवार।',
            },
            {
              english: 'Family through intersection of $S = 0$ and line $L = 0$:  $S + \\lambda L = 0$.',
        hindi: 'परिवार $S = 0$ और लाइन $L = 0$: $S + \\lambda L = 0$ के चौराहे से होकर गुजरता है।',
            },
            {
              english: 'Family through two points $A, B$:  $(x - x_1)(x - x_2) + (y - y_1)(y - y_2) + \\lambda L_{AB} = 0$.',
        hindi: 'दो बिंदुओं के माध्यम से परिवार $A, B$: $(x - x_1)(x - x_2) + (y - y_1)(y - y_2) + \\lambda L_{AB} = 0$।',
            },
            {
              english: 'Family touching a given line $L = 0$ at a point: known special form.',
        hindi: 'परिवार एक बिंदु पर दी गई रेखा $L = 0$ को छूता है: ज्ञात विशेष रूप।',
            },
          ],
        },
      ],
    },
    {
      id: 'two-circles',
      title: {
        english: 'Two Circles — Relative Position',
        hindi: 'दो वृत्त — सापेक्ष स्थिति',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Let $C_1, C_2$ be centres and $r_1, r_2$ radii. Let $d = |C_1 C_2|$.',
        hindi: 'मान लीजिए $C_1, C_2$ केंद्र और $r_1, r_2$ त्रिज्या है। चलो $d = |C_1 C_2|$.',
          },
        },
        {
          type: 'table',
          headers: ['Condition', 'Position'],
          rows: [
            ['$d > r_1 + r_2$', 'Circles **don\'t intersect** (one outside the other)'],
            ['$d = r_1 + r_2$', '**Externally touching** (3 common tangents)'],
            ['$|r_1 - r_2| < d < r_1 + r_2$', 'Intersect at **two points** (2 common tangents)'],
            ['$d = |r_1 - r_2|$', '**Internally touching** (1 common tangent)'],
            ['$d < |r_1 - r_2|$', 'One circle **inside** the other (no common tangent)'],
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Common chord & radical axis',
        hindi: 'सामान्य तार और मूल अक्ष',
          },
          statement: {
            english: 'For two circles $S_1 = 0,\\ S_2 = 0$ (with same coefficients of $x^2$ and $y^2$), the equation $S_1 - S_2 = 0$ is a **straight line** — the **radical axis** (common chord if circles intersect).',
        hindi: 'दो वृत्तों $S_1 = 0,\\\\ S_2 = 0$ ($x^2$ और $y^2$ के समान गुणांक के साथ) के लिए, समीकरण $S_1 - S_2 = 0$ एक **सीधी रेखा** है - **मूल अक्ष** (यदि वृत्त प्रतिच्छेद करते हैं तो सामान्य राग)।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Orthogonal circles',
        hindi: 'ओर्थोगोनल वृत्त',
          },
          body: {
            english: 'Two circles are **orthogonal** iff the tangents at a common point are perpendicular. Condition: $2(g_1 g_2 + f_1 f_2) = c_1 + c_2$.',
        hindi: 'दो वृत्त **ऑर्थोगोनल** होते हैं यदि एक उभयनिष्ठ बिंदु पर स्पर्श रेखाएं लंबवत हों। शर्त: $2(g_1 g_2 + f_1 f_2) = c_1 + c_2$.',
          },
        },
      ],
    },
    {
      id: 'examples',
      title: {
        english: 'Worked Examples',
        hindi: 'हल किये गये उदाहरण',
      },
      blocks: [
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'Find the equation of the circle passing through $(0, 0),\\ (1, 0),\\ (0, 1)$.',
        hindi: '$(0, 0),\\\\ (1, 0),\\\\ (0, 1)$ से गुजरने वाले वृत्त का समीकरण ज्ञात कीजिए।',
          },
          solution: {
            english: 'General form: $x^2 + y^2 + 2gx + 2fy + c = 0$. Substituting points: $c = 0,\\ 1 + 2g = 0 \\Rightarrow g = -1/2,\\ 1 + 2f = 0 \\Rightarrow f = -1/2$. Circle: $x^2 + y^2 - x - y = 0$.',
        hindi: 'सामान्य रूप: $x^2 + y^2 + 2gx + 2fy + c = 0$। स्थानापन्न बिंदु: $c = 0,\\ 1 + 2g = 0 \\Rightarrow g = -1/2,\\ 1 + 2f = 0 \\Rightarrow f = -1/2$. वृत्त: $x^2 + y^2 - x - y = 0$.',
          },
        },
        {
          type: 'example',
          number: 7,
          problem: {
            english: 'Find the equation of the tangent to $x^2 + y^2 = 25$ which is parallel to $3x - 4y = 7$.',
        hindi: '$x^2 + y^2 = 25$ की स्पर्शरेखा का समीकरण ज्ञात कीजिए जो $3x - 4y = 7$ के समानांतर है।',
          },
          solution: {
            english: 'Line: $3x - 4y + c = 0$. Tangent ⇒ $\\dfrac{|c|}{5} = 5 \\Rightarrow c = \\pm 25$. Tangents: $3x - 4y \\pm 25 = 0$.',
        hindi: 'पंक्ति: $3x - 4y + c = 0$। स्पर्शरेखा ⇒ $\\dfrac{|c|}{5} = 5 \\Rightarrow c = \\pm 25$. स्पर्शरेखाएँ: $3x - 4y \\pm 25 = 0$।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '(x-h)^2 + (y-k)^2 = r^2',
      desc: {
        english: 'Standard form',
        hindi: 'मानक प्रपत्र',
      },
    },
    {
      latex: 'x^2 + y^2 + 2gx + 2fy + c = 0',
      desc: {
        english: 'General form (centre (-g, -f), r = √(g²+f²-c))',
        hindi: 'सामान्य रूप (केंद्र (-g, -f), r = √(g²+f²-c))',
      },
    },
    {
      latex: 'L = \\sqrt{S_1}',
      desc: {
        english: 'Length of tangent from external point',
        hindi: 'बाहरी बिंदु से स्पर्शरेखा की लंबाई',
      },
    },
    {
      latex: 'T = 0',
      desc: {
        english: 'Tangent / chord of contact',
        hindi: 'स्पर्शरेखा/संपर्क का तार',
      },
    },
    {
      latex: 'SS_1 = T^2',
      desc: {
        english: 'Pair of tangents',
        hindi: 'स्पर्शरेखाओं का युग्म',
      },
    },
    {
      latex: 'T = S_1',
      desc: {
        english: 'Chord bisected at (x₁, y₁)',
        hindi: 'जीवा (x₁, y₁) पर द्विभाजित',
      },
    },
    {
      latex: 'c^2 = r^2(1+m^2)',
      desc: {
        english: 'Tangency of y = mx + c to x²+y² = r²',
        hindi: 'y = mx + c से x²+y² = r² की स्पर्शरेखा',
      },
    },
    {
      latex: '2(g_1 g_2 + f_1 f_2) = c_1 + c_2',
      desc: {
        english: 'Orthogonality condition',
        hindi: 'ऑर्थोगोनैलिटी स्थिति',
      },
    },
  ],
  pyqs: [
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Tangent length',
      problem: {
        english: 'Length of tangent from $(2, 3)$ to circle $x^2 + y^2 - 4x - 6y - 12 = 0$ is:',
        hindi: '$(2, 3)$ से वृत्त $x^2 + y^2 - 4x - 6y - 12 = 0$ तक स्पर्शरेखा की लंबाई है:',
      },
      answer: {
        english: '$\\sqrt{S_1}$ where $S_1 = 4 + 9 - 8 - 18 - 12 = -25$. **Negative ⇒ point is inside; no real tangent.**',
        hindi: '$\\sqrt{S_1}$ जहां $S_1 = 4 + 9 - 8 - 18 - 12 = -25$। **नकारात्मक ⇒ बिंदु अंदर है; कोई वास्तविक स्पर्शरेखा नहीं.**',
      },
      solution: {
        english: 'Inside the circle (no tangent). Re-check problem statement; in Main 2023 the answer turned out non-real for typical variants.',
        hindi: 'वृत्त के अंदर (कोई स्पर्शरेखा नहीं)। समस्या विवरण की पुनः जाँच करें; मुख्य 2023 में उत्तर विशिष्ट वेरिएंट के लिए गैर-वास्तविक निकला।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Equation of circle',
      problem: {
        english: 'Equation of the circle passing through origin and cutting intercepts of length $a$ and $b$ on $x$- and $y$-axes is:',
        hindi: 'मूल बिंदु से गुजरने वाले और $a$ और $b$ लंबाई के अंतःखंडों को $x$- और $y$-अक्षों पर काटने वाले वृत्त का समीकरण है:',
      },
      answer: {
        english: '$x^2 + y^2 - ax - by = 0$',
        hindi: '$x^2 ​​+ y^2 - ax - द्वारा = 0$',
      },
      solution: {
        english: 'Such a circle has centre $(a/2, b/2)$ and passes through origin: $r = \\tfrac{1}{2}\\sqrt{a^2 + b^2}$. Expand the standard form.',
        hindi: 'ऐसे वृत्त का केंद्र $(a/2, b/2)$ है और यह मूल बिंदु से होकर गुजरता है: $r = \\tfrac{1}{2}\\sqrt{a^2 + b^2}$। मानक प्रपत्र का विस्तार करें. वृत्त $(a/2, b/2)$ और $r = \\tfrac{1}{2}\\sqrt{a^2 + b^2}$ एक दूसरे को स्पर्श करते हैं:',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Two circles',
      problem: {
        english: 'Circles $x^2 + y^2 = 4$ and $x^2 + y^2 - 6x - 8y + 24 = 0$ touch each other:',
        hindi: 'वृत्त $x^2 + y^2 = 4$ और $x^2 + y^2 - 6x - 8y + 24 = 0$ एक दूसरे को स्पर्श करते हैं:',
      },
      answer: {
        english: 'Externally',
        hindi: 'बाह्य रूप से',
      },
      solution: {
        english: '$C_1 = (0,0), r_1 = 2$. $C_2 = (3, 4), r_2 = 1$. $d = 5 = r_1 + r_2$ ⇒ externally touching.',
        hindi: '$C_1 = (0,0), r_1 = 2$. $C_2 = (3, 4), r_2 = 1$। $d = 5 = r_1 + r_2$ ⇒ बाह्य रूप से स्पर्श करना।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Chord',
      problem: {
        english: 'Length of chord cut by line $x + y = 2$ from circle $x^2 + y^2 = 5$ is:',
        hindi: 'वृत्त $x^2 + y^2 = 5$ से रेखा $x + y = 2$ द्वारा काटी गई जीवा की लंबाई है:',
      },
      answer: {
        english: '$2\\sqrt{3}$',
        hindi: '$2\\sqrt{3}$',
      },
      solution: {
        english: '$d = 2/\\sqrt 2 = \\sqrt 2$. Chord $= 2\\sqrt{5 - 2} = 2\\sqrt 3$.',
        hindi: '$d = 2/\\sqrt 2 = \\sqrt 2$. कॉर्ड $= 2\\sqrt{5 - 2} = 2\\sqrt 3$.',
      },
    },
    {
      year: 2019,
      exam: 'JEE Advanced',
      tag: 'Orthogonality',
      problem: {
        english: 'For what value of $\\lambda$ are the circles $x^2 + y^2 + 2x + 2y + 1 = 0$ and $x^2 + y^2 + 4x + 4y + \\lambda = 0$ orthogonal?',
        hindi: '$\\lambda$ के किस मान के लिए वृत्त $x^2 + y^2 + 2x + 2y + 1 = 0$ और $x^2 + y^2 + 4x + 4y + \\lambda = 0$ ऑर्थोगोनल हैं?',
      },
      answer: {
        english: '$\\lambda = -7$',
        hindi: '$\\lambda = -7$',
      },
      solution: {
        english: '$2(g_1 g_2 + f_1 f_2) = c_1 + c_2 \\Rightarrow 2(1 \\cdot 2 + 1 \\cdot 2) = 1 + \\lambda \\Rightarrow 8 = 1 + \\lambda \\Rightarrow \\lambda = 7$. (Sign check on conventions.)',
        hindi: '$2(g_1 g_2 + f_1 f_2) = c_1 + c_2 \\Rightarrow 2(1 \\cdot 2 + 1 \\cdot 2) = 1 + \\lambda \\Rightarrow 8 = 1 + \\lambda \\Rightarrow \\lambda = 7$. (सम्मेलनों पर हस्ताक्षर करें।)',
      },
    },
  ],
  next: {
    slug: 'conic-sections',
    title: 'Conic Sections',
  },
};

export default chapter;
